import React, { useState, useEffect, useCallback } from 'react';
import { AuditService } from '../../services/audit.service';
import {
    ShieldCheckIcon,
    ExclamationTriangleIcon,
    CalendarDaysIcon,
    DocumentArrowDownIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

interface AuditLog {
    id: number;
    event_type: string;
    user_id?: string;
    action: string;
    resource?: string;
    resource_type?: string;
    resource_id?: string;
    details?: any;
    ip_address?: string;
    user_agent?: string;
    risk_level: string;
    created_at: string;
    user_email?: string;
    display_email?: string;
    success?: boolean;
}

interface SecurityAlert {
    id: number;
    event_type: string;
    user_email?: string;
    display_email?: string;
    action: string;
    risk_level: string;
    created_at: string;
    details?: any;
    ip_address?: string;
}

const AuditDashboard: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filters, setFilters] = useState({
        event_type: '',
        risk_level: '',
        search: '',
        start_date: '',
        end_date: ''
    });

    // Activity summary stats
    const [stats, setStats] = useState({
        totalLogs: 0,
        failedLogins: 0,
        highRiskEvents: 0,
        todayActivity: 0,
        weekActivity: 0
    });

    // Debounced search state
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Update filters when debounced search changes
    useEffect(() => {
        if (debouncedSearch !== filters.search) {
            setFilters(prev => ({
                ...prev,
                search: debouncedSearch
            }));
            setCurrentPage(1); // Reset to first page when search changes
        }
    }, [debouncedSearch]);

    // Fetch data when page or filters change
    useEffect(() => {
        fetchData();
    }, [currentPage, filters]);

    const fetchData = useCallback(async () => {
        try {
            if (filters.search && filters.search.trim()) {
                setSearching(true);
            } else {
            setLoading(true);
            }
            setError('');

            // Fetch audit logs with pagination
            const auditFilters = {
                event_type: filters.event_type || undefined,
                risk_level: filters.risk_level || undefined,
                search: filters.search || undefined,
                date_from: filters.start_date || undefined,
                date_to: filters.end_date || undefined
            };

            console.log('Fetching audit logs with filters:', auditFilters);

            const logsResponse = await AuditService.getLogs(auditFilters, {
                page: currentPage,
                limit: 20
            });

            console.log('Audit logs response:', logsResponse);

            setLogs(logsResponse.logs || []);
            setTotalPages(logsResponse.pagination?.total_pages || 1);
            setTotalRecords(logsResponse.pagination?.total_records || 0);

            // Update stats from the response (overall stats, not filtered)
            const statistics = logsResponse.statistics || {};
            
            setStats({
                totalLogs: statistics.total_events || 0,
                failedLogins: statistics.failed_logins || 0,
                highRiskEvents: statistics.high_risk_events || 0,
                todayActivity: statistics.today_events || 0,
                weekActivity: statistics.week_events || 0
            });

            // Fetch additional stats for alerts (only on first page and no filters)
            if (currentPage === 1 && !hasActiveFilters()) {
            try {
                const statsResponse = await AuditService.getStats();
                if (statsResponse.recent_high_risk) {
                        setAlerts(statsResponse.recent_high_risk.slice(0, 5));
                }
            } catch (statsError) {
                console.warn('Could not fetch additional stats:', statsError);
                    // Don't show error to user for secondary stats, just log it
                }
            }

        } catch (err: any) {
            console.error('Error fetching audit data:', err);
            
            // Provide more specific error messages
            let errorMessage = 'Failed to fetch audit data';
            if (err.message) {
                if (err.message.includes('Network connection failed')) {
                    errorMessage = 'Network connection failed. Please check your internet connection and try again.';
                } else if (err.message.includes('authentication')) {
                    errorMessage = 'Authentication failed. Please log in again.';
                } else if (err.message.includes('HTTP 403')) {
                    errorMessage = 'Access denied. You may not have permission to view audit logs.';
                } else if (err.message.includes('HTTP 500')) {
                    errorMessage = 'Server error occurred. Please try again later.';
                } else {
                    errorMessage = err.message;
                }
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
            setSearching(false);
        }
    }, [currentPage, filters]);

    const hasActiveFilters = () => {
        return !!(filters.event_type || filters.risk_level || filters.search || filters.start_date || filters.end_date);
    };

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentPage(1); // Reset to first page when filtering
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
    };

    const exportLogs = async () => {
        try {
            const exportFilters = {
                date_from: filters.start_date || undefined,
                date_to: filters.end_date || undefined,
                event_type: filters.event_type || undefined,
                risk_level: filters.risk_level || undefined,
                search: filters.search || undefined
            };
            await AuditService.exportLogs(exportFilters);
        } catch (err: any) {
            setError('Failed to export logs: ' + err.message);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getRiskBadge = (riskLevel: string) => {
        const colorClass = AuditService.getRiskLevelColor(riskLevel);
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                {riskLevel.toUpperCase()}
            </span>
        );
    };

    const clearFilters = () => {
        setFilters({
            event_type: '',
            risk_level: '',
            search: '',
            start_date: '',
            end_date: ''
        });
        setSearchQuery('');
        setCurrentPage(1);
    };

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    if (loading && logs.length === 0) {
        return (
            <div className="bg-gray-50 dark:bg-gray-900 flex items-center justify-center min-h-96">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 dark:bg-gray-900">
            {/* Hero Banner Section - Matching VaultPage design */}
            <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                    <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                    <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                </div>

                <div className="relative px-6 py-12">
                    <div className="max-w-7xl mx-auto">
                        {/* Header Content */}
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                            <div className="mb-6 lg:mb-0">
                                <div className="flex items-center mb-4">
                                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl mr-4">
                                        <ShieldCheckIcon className="h-8 w-8 text-white" />
                                    </div>
                                    <h1 className="text-4xl lg:text-5xl font-bold text-white font-sans">
                                        Security Audit Dashboard
                                    </h1>
                                </div>
                                <p className="text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
                                    Monitor and analyze all security events, track authentication attempts, 
                                    and maintain comprehensive audit trails across your password manager.
                                </p>
                            </div>
                            
                            {/* Action Controls */}
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={exportLogs}
                                    className="flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                                    Export CSV
                                </button>
                            </div>
                        </div>

                {/* Stats Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                            {/* Total Events */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-white/20 rounded-lg">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            {stats.totalLogs.toLocaleString()}
                                        </div>
                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                            Total Events
                                </div>
                            </div>
                        </div>
                    </div>

                            {/* Failed Logins */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-red-500/30 rounded-lg">
                                        <ExclamationTriangleIcon className="w-6 h-6 text-red-200" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            {stats.failedLogins}
                                </div>
                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                            Failed Logins
                                </div>
                            </div>
                        </div>
                    </div>

                            {/* High Risk Events */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-orange-500/30 rounded-lg">
                                        <svg className="w-6 h-6 text-orange-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            {stats.highRiskEvents}
                                        </div>
                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                            High Risk
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Today's Activity */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-green-500/30 rounded-lg">
                                        <CalendarDaysIcon className="w-6 h-6 text-green-200" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            {stats.todayActivity}
                        </div>
                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                            Today
                    </div>
                                    </div>
                                </div>
                            </div>

                            {/* Week Activity */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-blue-500/30 rounded-lg">
                                        <svg className="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            {stats.weekActivity}
                                        </div>
                                        <div className="text-xs text-white/80 uppercase tracking-wide">
                                            This Week
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="container mx-auto px-6 py-8">
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                        <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                {/* Security Alerts */}
                {alerts.length > 0 && (
                    <div className="mb-8">
                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-orange-500">
                                <h3 className="text-lg font-semibold text-white flex items-center">
                                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                    Recent Security Alerts
                                </h3>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3">
                                    {alerts.map((alert, index) => (
                                        <div key={index} className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                            <div className="flex items-center space-x-3">
                                                <span className="text-2xl">
                                                    {AuditService.getEventTypeIcon(alert.event_type)}
                                                </span>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {alert.action} by {alert.display_email || alert.user_email || 'Unknown User'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatDate(alert.created_at)} • {alert.ip_address}
                                                    </p>
                                                </div>
                                            </div>
                                                {getRiskBadge(alert.risk_level)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters and Controls */}
                <div className="mb-6 bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-800">
                        <h3 className="text-lg font-semibold text-white flex items-center">
                            <FunnelIcon className="h-5 w-5 mr-2" />
                            Filters & Search
                        </h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                        <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Type</label>
                            <select
                                value={filters.event_type}
                                onChange={(e) => handleFilterChange('event_type', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Events</option>
                                <option value="authentication">Authentication</option>
                                <option value="data_access">Data Access</option>
                                <option value="administration">Administration</option>
                            </select>
                        </div>

                        <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Risk Level</label>
                            <select
                                value={filters.risk_level}
                                onChange={(e) => handleFilterChange('risk_level', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Levels</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>

                        <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                            <input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                            <input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search</label>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by event, action, user, IP, or details..."
                                        value={searchQuery}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                    />
                                    {(searching || (searchQuery && debouncedSearch !== searchQuery)) && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                                        </div>
                                    )}
                                </div>
                                {debouncedSearch && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Searching for: "{debouncedSearch}"
                                    </p>
                                )}
                            </div>
                            <div className="flex items-end space-x-2 flex-shrink-0">
                            <button
                                    onClick={clearFilters}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
                            >
                                    Clear All
                            </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Audit Logs Table */}
                <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-800">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-white">
                                Audit Logs ({totalRecords.toLocaleString()} total)
                        </h3>
                            <span className="text-white/80 text-sm">
                                Page {currentPage} of {totalPages}
                            </span>
                        </div>
                    </div>
                    
                        <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Event
                                        </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            User
                                        </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Action
                                        </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Resource
                                        </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Risk
                                        </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Time
                                        </th>
                                    </tr>
                                </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                <div className="flex items-center">
                                                <span className="mr-2 text-lg">
                                                        {AuditService.getEventTypeIcon(log.event_type)}
                                                    </span>
                                                <div>
                                                    <div>{log.event_type.replace('_', ' ').toUpperCase()}</div>
                                                    {log.success === false && (
                                                        <span className="text-xs text-red-500 font-medium">FAILED</span>
                                                    )}
                                                </div>
                                                </div>
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            <div>
                                                <div className="font-medium">{log.display_email || log.user_email || 'System'}</div>
                                                {log.ip_address && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{log.ip_address}</div>
                                                )}
                                            </div>
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            <div>
                                                <div className="font-medium">{log.action}</div>
                                                {log.resource_type && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {log.resource_type}
                                                        {log.resource_id && `: ${log.resource_id.substring(0, 8)}...`}
                                                    </div>
                                                )}
                                            </div>
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {log.resource || log.resource_type || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getRiskBadge(log.risk_level)}
                                            </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            <div className="text-xs">
                                                {formatDate(log.created_at)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-600">
                                <div className="flex-1 flex justify-between sm:hidden">
                                    <button
                                    onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                                        Previous
                                    </button>
                                    <button
                                    onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                                    </button>
                                </div>
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        Showing <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> to{' '}
                                        <span className="font-medium">{Math.min(currentPage * 20, totalRecords)}</span> of{' '}
                                        <span className="font-medium">{totalRecords.toLocaleString()}</span> results
                                        {hasActiveFilters() && (
                                            <span className="ml-2 text-primary-600 dark:text-primary-400">(filtered)</span>
                                        )}
                                        </p>
                                    </div>
                                <div className="flex items-center space-x-2">
                                    {/* First page */}
                                    {currentPage > 3 && (
                                        <>
                                            <button
                                                onClick={() => goToPage(1)}
                                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >
                                                1
                                            </button>
                                            {currentPage > 4 && (
                                                <span className="px-2 text-gray-500">...</span>
                                            )}
                                        </>
                                    )}

                                    {/* Previous page button */}
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            >
                                        <ChevronLeftIcon className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Page numbers */}
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                                        if (page > totalPages) return null;
                                        
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => goToPage(page)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                                                    page === currentPage
                                                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900 dark:border-primary-400 dark:text-primary-300'
                                                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                    
                                    {/* Next page button */}
                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRightIcon className="h-4 w-4" />
                                    </button>

                                    {/* Last page */}
                                    {currentPage < totalPages - 2 && (
                                        <>
                                            {currentPage < totalPages - 3 && (
                                                <span className="px-2 text-gray-500">...</span>
                                            )}
                                            <button
                                                onClick={() => goToPage(totalPages)}
                                                className="relative inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}

                                    {/* Page jump input */}
                                    <div className="flex items-center ml-4">
                                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Go to:</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value=""
                                            placeholder="Page"
                                            onChange={(e) => {
                                                const page = parseInt(e.target.value);
                                                if (page >= 1 && page <= totalPages) {
                                                    goToPage(page);
                                                    e.target.value = '';
                                                }
                                            }}
                                            className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default AuditDashboard;
